import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Event as EventIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { format, formatDistanceToNowStrict } from 'date-fns';
import {
  getManufacturingQueue,
  getPartnerDashboard,
  getPartnerProfile,
} from '../../api/partnerPortal';
import { usePartnerAuth } from '../../contexts/PartnerAuthContext';

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'MMM d, yyyy • h:mm a');
  } catch {
    return value;
  }
};

const formatDistanceFromNow = (value) => {
  if (!value) return '—';
  try {
    return formatDistanceToNowStrict(new Date(value), { addSuffix: true });
  } catch {
    return value;
  }
};

const metricDefinitions = [
  { key: 'total_orders', label: 'Total Orders', icon: <AssignmentIcon />, color: 'primary.main' },
  { key: 'active_orders', label: 'Active Orders', icon: <TrendingUpIcon />, color: 'info.main' },
  { key: 'queued', label: 'Queued', icon: <AssignmentIcon />, color: 'warning.main' },
  { key: 'in_production', label: 'In Production', icon: <AssignmentIcon />, color: 'success.main' },
  { key: 'quality_check', label: 'In QA', icon: <AssignmentIcon />, color: 'secondary.main' },
  { key: 'completed_last_30_days', label: 'Completed (30d)', icon: <TrendingUpIcon />, color: 'primary.dark' },
  { key: 'pending_tech_packs', label: 'Awaiting Tech Pack', icon: <AssignmentIcon />, color: 'error.main' },
];

const PartnerDashboardPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { partner, syncProfile } = usePartnerAuth();

  const { data: profileData, isFetching: isFetchingProfile } = useQuery(
    ['partner-profile'],
    getPartnerProfile,
    {
      initialData: partner?.profile,
      onSuccess: (data) => {
        if (data) {
          syncProfile?.(data);
        }
        return data;
      },
    }
  );

  const {
    data: dashboardData,
    isLoading: isLoadingDashboard,
    isFetching: isFetchingDashboard,
    refetch: refetchDashboard,
    error: dashboardError,
  } = useQuery(['partner-dashboard'], getPartnerDashboard, {
    refetchOnWindowFocus: false,
  });

  const {
    data: queueData,
    isLoading: isLoadingQueue,
    refetch: refetchQueue,
  } = useQuery(['partner-manufacturing-queue'], getManufacturingQueue, {
    refetchOnWindowFocus: false,
  });

  const partnerName =
    partner?.company_name ||
    profileData?.company_info?.legalName ||
    partner?.profile?.company_info?.legalName ||
    'Studio Sivad Partner';

  const stats = dashboardData?.stats ?? {};
  const upcomingDeadlines = dashboardData?.upcoming_deadlines ?? [];
  const recentActivity = dashboardData?.recent_activity ?? [];
  const queue = queueData?.queue ?? [];

  const queueSummary = useMemo(() => {
    if (!queueData?.queue_summary) return null;
    return queueData.queue_summary;
  }, [queueData]);

  const handleRefresh = async () => {
    await Promise.all([
      refetchDashboard(),
      refetchQueue(),
      queryClient.invalidateQueries(['partner-profile']),
    ]);
  };

  const isBusy = isLoadingDashboard || isLoadingQueue;

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {partnerName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor production velocity, deadlines, and quality performance across your Studio
            Sivad work queue.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" gap={1}>
          {(isFetchingDashboard || isFetchingProfile) && <CircularProgress size={20} />}
          <Tooltip title="Refresh dashboard data">
            <span>
              <IconButton onClick={handleRefresh} disabled={isBusy}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {dashboardError && (
        <Alert severity="error">
          Unable to load dashboard data. Please try again or contact the Studio Sivad support team.
        </Alert>
      )}

      <Grid container spacing={2}>
        {metricDefinitions.map((metric) => (
          <Grid item key={metric.key} xs={12} sm={6} md={3} lg={3}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    bgcolor: `${metric.color}16`,
                    color: metric.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {metric.icon}
                </Box>
                <Typography variant="subtitle2" color="text.secondary">
                  {metric.label}
                </Typography>
              </Stack>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {stats[metric.key] ?? 0}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Upcoming Deadlines
              </Typography>
              <Chip
                icon={<EventIcon fontSize="small" />}
                label={`${upcomingDeadlines.length || 0} in pipeline`}
                size="small"
              />
            </Stack>

            {upcomingDeadlines.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                <Typography variant="body2">
                  No deadlines scheduled. When Studio Sivad assigns projects to your facility,
                  they will appear here with estimated completion dates.
                </Typography>
              </Box>
            ) : (
              <List disablePadding sx={{ display: 'grid', gap: 1.5 }}>
                {upcomingDeadlines.map((deadline) => (
                  <Paper
                    key={deadline.orderId}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                    }}
                    onClick={() => navigate(`/partner/orders?highlight=${deadline.orderId}`)}
                  >
                    <ListItem
                      disableGutters
                      secondaryAction={
                        <Chip
                          label={formatDistanceFromNow(deadline.estimated_completion)}
                          size="small"
                          color={deadline.priority === 'rush' ? 'error' : 'default'}
                        />
                      }
                    >
                      <ListItemText
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Order {deadline.orderId}
                          </Typography>
                        }
                        secondary={
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                              Target completion: {formatDateTime(deadline.estimated_completion)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Status: {deadline.status || 'Queued'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Priority: {deadline.priority || 'standard'}
                            </Typography>
                          </Stack>
                        }
                      />
                    </ListItem>
                  </Paper>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Queue Snapshot
              </Typography>
              {queueSummary && (
                <Chip
                  label={`${queueSummary.total_orders || queue.length} active`}
                  size="small"
                  color="primary"
                />
              )}
            </Stack>

            {isLoadingQueue ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 120,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : queue.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No orders are currently assigned to your facility. Studio Sivad will notify you when
                new tech packs and production runs become available.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {queue.slice(0, 5).map((item) => (
                  <Paper
                    key={item.orderId}
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: 2,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {item.orderId}
                      </Typography>
                      <Chip
                        label={item.status || 'queued'}
                        size="small"
                        color={
                          item.status === 'in_production'
                            ? 'success'
                            : item.status === 'rush'
                              ? 'error'
                              : 'default'
                        }
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                      <Typography variant="caption" color="text.secondary">
                        Queue position: {item.queue_position ?? '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Priority: {item.priority || 'standard'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ETA: {item.estimated_production_time || '—'}
                      </Typography>
                    </Stack>
                  </Paper>
                ))}
                {queue.length > 5 && (
                  <Typography
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate('/partner/orders')}
                  >
                    View full queue →
                  </Typography>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Activity
          </Typography>
          <Chip
            icon={<TrendingUpIcon fontSize="small" />}
            label={`${recentActivity.length} updates`}
            size="small"
          />
        </Stack>

        {recentActivity.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Production events and status changes will appear here as your team progresses through
            orders.
          </Typography>
        ) : (
          <List disablePadding sx={{ display: 'grid', gap: 1 }}>
            {recentActivity.map((activity) => (
              <Paper
                key={`${activity.orderId}-${activity.updated_at}`}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => navigate(`/partner/orders?highlight=${activity.orderId}`)}
              >
                <ListItem disableGutters>
                  <ListItemText
                    primary={
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                      >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Order {activity.orderId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Updated {formatDistanceFromNow(activity.updated_at)}
                        </Typography>
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        Status: {activity.status || 'queued'} • Queue position:{' '}
                        {activity.queue_position ?? '—'} • Priority: {activity.priority || 'standard'}
                      </Typography>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default PartnerDashboardPage;
